/**
 * The enlarged headshot.
 *
 * A 32px avatar on a roster row is enough to jog a teacher's memory, but not enough to tell
 * two similar-looking children apart — which is the case this feature exists for. Both
 * moments that matter, check-in and check-out, start from the roster row, so the enlarged
 * view hangs off the avatar rather than off either action: the teacher confirms the face
 * first, then swipes.
 */

import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { X } from 'lucide-react';

interface Props {
  name: string;
  /** Signed URL. The lightbox is never opened without one. */
  photoUrl: string;
  /** Grade, room, program — whatever else helps confirm this is the right child. */
  subtitle?: string | null;
  onClose: () => void;
}

export function StudentPhotoLightbox({ name, photoUrl, subtitle, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portalled to `document.body` for the reason under `MOBILE_CHROME_ON_BODY` in
  // `StaffLayout`. Both portals mount the page inside pull-to-refresh's `overflow: hidden`
  // wrappers, and on iOS a clip in the ancestry keeps a fixed element out of the
  // viewport-fixed layer the bottom bars live in — so a full-screen overlay left in the page
  // paints *under* them however high its z-index, there being no shared layer to compare in.
  return createPortal(
    <div
      // Above the check-out sheet (z-[60]), which can be the thing that opened it.
      className="fixed inset-0 z-[70] bg-black/80 flex flex-col items-center justify-center p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo of ${name}`}
    >
      <button
        onClick={onClose}
        aria-label="Close photo"
        className="absolute top-4 right-4 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10"
      >
        <X className="h-6 w-6" />
      </button>

      <img
        src={photoUrl}
        alt={name}
        // Held below the 512px stored size so the browser only ever downscales. Upscaling
        // past the source is the one thing that would undo the point of storing it large.
        className="w-full max-w-[380px] aspect-square object-cover rounded-2xl shadow-2xl bg-gray-800"
      />

      <p className="mt-4 text-lg font-semibold text-white text-center">{name}</p>
      {subtitle && <p className="mt-0.5 text-sm text-white/70 text-center">{subtitle}</p>}
      <p className="mt-3 text-xs text-white/40">Tap anywhere to close</p>
    </div>,
    document.body,
  );
}
